import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import HealthMeasurementGauge from "@/features/MyHealth/components/HealthMeasurementGauge";
import {
  getHealthMeasurementValueRange,
  type HealthMeasurementQuestionType,
} from "@/services/core-api-adapter";
import { HealthMeasurementIdentifier } from "@/store";
import { getHealthMeasurementValueRangeColors } from "@/theme";

const START_ANGLE = 90;
const END_ANGLE = 270;
const ARC_DEGREES = END_ANGLE - START_ANGLE;

const SEGMENT_ARC_WEIGHTS = [
  { min: 0, max: 50, weight: 1 },
  { min: 50, max: 60, weight: 1 },
  { min: 61, max: 90, weight: 1 },
  { min: 91, max: 180, weight: 1 },
];
const TOTAL_WEIGHT = SEGMENT_ARC_WEIGHTS.reduce((s, seg) => s + seg.weight, 0);

function heartRateValueToAngle(value: number): number {
  if (value <= 0) return START_ANGLE;
  if (value >= 180) return END_ANGLE;
  if (value > 60 && value < 61) return heartRateValueToAngle(60);
  let accumulated = 0;
  for (const seg of SEGMENT_ARC_WEIGHTS) {
    if (value <= seg.max) {
      const t = (value - seg.min) / (seg.max - seg.min);
      return (
        START_ANGLE +
        ((accumulated + t * seg.weight) / TOTAL_WEIGHT) * ARC_DEGREES
      );
    }
    accumulated += seg.weight;
  }
  return END_ANGLE;
}

function segmentTicks(from: number, to: number, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i <= count; i++) {
    out.push(from + (to - from) * (i / count));
  }
  return out;
}

function getHeartRateTickValues(): number[] {
  const a = segmentTicks(0, 50, 6);
  const b = segmentTicks(50, 60, 4);
  const c = segmentTicks(61, 90, 6);
  const d = segmentTicks(91, 180, 6);
  return Array.from(new Set([...a, ...b, ...c, ...d])).sort((x, y) => x - y);
}

const HEART_RATE_TICK_VALUES = getHeartRateTickValues();
const HEART_RATE_TICK_LABELS = [0, 50, 60, 90, 180];

function getValueRangeWithColors() {
  const valueRange = getHealthMeasurementValueRange("Pulse");
  const colorMap = getHealthMeasurementValueRangeColors() as Record<
    string,
    Record<string, string>
  >;
  const key: HealthMeasurementQuestionType = "Pulse";
  return valueRange.map((item) => ({
    ...item,
    color: colorMap[key]?.[item.healthRiskIndicator],
  }));
}

export interface CurrentHeartRateProps {
  value: number;
  unit?: string;
}

export default function CurrentHeartRate({
  value,
  unit = "",
}: CurrentHeartRateProps) {
  const { t } = useTranslation();
  const valueRange = useMemo(() => getValueRangeWithColors(), []);

  return (
    <Stack alignItems="center">
      <HealthMeasurementGauge
        healthMeasurementIdentifier={HealthMeasurementIdentifier.Pulse}
        value={value}
        unit={unit}
        valueRange={valueRange}
        numberOfTicks={HEART_RATE_TICK_VALUES.length}
        tickValues={HEART_RATE_TICK_VALUES}
        tickLabelValues={HEART_RATE_TICK_LABELS}
        valueToAngle={heartRateValueToAngle}
      />
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 16,
          lineHeight: 1.25,
        }}
      >
        {t("HeartRate.manualEntry.inputLabel")}
      </Typography>
    </Stack>
  );
}
